import { ReactNode } from "react";
import ReactFocusLock from "react-focus-lock";
import { RemoveScroll } from "react-remove-scroll";
import { LinearProgress } from "./LinearProgress";
import { DialogCloseButton } from "./DialogCloseButton";
import styles from "./DialogContent.module.scss";

export type ShowCloseButton = "no" | "yes" | "disabled";

type DialogContentProps = {
  className?: string;
  label?: string;
  children: ReactNode;
  title?: string;
  footer?: ReactNode;
  description?: string;
  onDismiss?: () => void;
  showCloseButton?: ShowCloseButton;
  showBusy?: boolean;
  ariaBusy?: boolean;
  role?: string;
  closeBtnLabel?: string;
};

export const DialogContent = ({
  title,
  label = title,
  className = "",
  onDismiss,
  children,
  footer,
  showCloseButton = "yes",
  showBusy = false,
  ariaBusy,
  role,
  description = "Beginning of dialog window. Escape will cancel and close the window.",
  closeBtnLabel,
}: DialogContentProps) => {
  const shouldShowCloseButton =
    showCloseButton === "yes" || showCloseButton === "disabled";
  const isCloseButtonDisabled = showCloseButton === "disabled";

  return (
    <ReactFocusLock autoFocus returnFocus>
      <RemoveScroll>
        <div
          aria-modal="true"
          role={role}
          aria-busy={ariaBusy}
          aria-label={label}
          tabIndex={-1}
          className={`${className} ${styles.container} ${styles.modal__container} modal__container_scrollable`}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <div className="modal__content">
            <div className="sr-only">{description}</div>

            {title && (
              <div className="modal__header ">
                <h2 className="modal__title">{title}</h2>
              </div>
            )}

            <div className={`modal__body ${styles.modal__body}`}>
              {children}
            </div>

            <div className="modal__eyebrow">
              {showBusy && <LinearProgress />}
              {shouldShowCloseButton && (
                <DialogCloseButton
                  disabled={isCloseButtonDisabled}
                  onClick={onDismiss}
                  ariaLabel={closeBtnLabel}
                />
              )}
            </div>

            {footer && (
              <>
                <div className="modal__divider divider" />
                {footer}
              </>
            )}
          </div>
        </div>
      </RemoveScroll>
    </ReactFocusLock>
  );
};
